import { useState, useEffect } from "react";
import { MessageSquare, Plus, RefreshCw } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { API, getAuthHeaders, formatDateTimeFR } from "./helpers";
import MailOriginePanneau from "../../omnihub/communication/MailOriginePanneau";
import Bouton from "../../omnihub/communication/boutonBureau";

// T-364 (F-4) : les échanges du Bureau AMO sont des commentaires étiquetés [MAIL-AMO] / [REPONSE-AMO] / [ACTION-AMO] (pas de second journal).
export const ETIQUETTES_ECHANGES = ["MAIL-AMO", "REPONSE-AMO", "ACTION-AMO"];
export const estEchange = (c) => ETIQUETTES_ECHANGES.some((t) => String(c?.contenu || "").startsWith(`[${t}]`));
// T-404 : la clé d'idempotence d'un échange est « amo:<fiche>:<nature> » ; la fiche porte le mail d'origine. Sans clé (très ancien commentaire) : « introuvable ».
export const refMailDuCommentaire = (c) => { const m = /^amo:([^:]+):/.exec(String(c?.cle_idempotence || "")); return m ? `fiche:${m[1]}` : ""; };

export function CommentairesDossier({ dossierId, tenantId, verrouille, filtre, onFiltre }) {
  const [filtreLocal, setFiltreLocal] = useState("tous");
  const filtreActif = filtre ?? filtreLocal;
  const choisirFiltre = (f) => (onFiltre ? onFiltre(f) : setFiltreLocal(f));
  const [commentaires, setCommentaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [adding, setAdding] = useState(false);
  const [mailVu, setMailVu] = useState(null);   // { ref } quand le panneau « Mail d'origine » est ouvert

  useEffect(() => { loadCommentaires(); }, [dossierId]);

  const loadCommentaires = async () => {
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/commentaires`, { headers, credentials: 'include' });
      if (res.ok) setCommentaires(await res.json());
    } catch (err) { console.error("Erreur chargement commentaires:", err); }
    finally { setLoading(false); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setAdding(true);
    try {
      const headers = getAuthHeaders(tenantId, "application/json");
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/commentaires`, {
        method: "POST", headers, credentials: 'include',
        body: JSON.stringify({ contenu: newComment })
      });
      if (res.ok) {
        const comment = await res.json();
        setCommentaires([comment, ...commentaires]);
        setNewComment("");
      }
    } catch (err) { console.error("Erreur ajout commentaire:", err); }
    finally { setAdding(false); }
  };

  const visibles = filtreActif === "echanges" ? commentaires.filter(estEchange) : commentaires;

  const fmtDate = (dateStr) => {
    if (!dateStr) return "-";
    return formatDateTimeFR(dateStr);
  };

  return (
    <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl p-6">
      <h3 className="text-[var(--oh-text)] font-medium mb-4">Commentaires du dossier</h3>
      {!verrouille && (
        <div className="flex gap-2 mb-6">
          <textarea placeholder="Ajouter un commentaire..." value={newComment} onChange={(e) => setNewComment(e.target.value)}
            className="flex-1 min-h-[80px] px-3 py-2 bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg text-[var(--oh-text)] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500" />
          <Button onClick={handleAddComment} disabled={!newComment.trim() || adding} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] self-end">
            {adding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
      )}
      <div className="flex gap-2 mb-4" role="group" aria-label="Filtrer les commentaires" data-testid="commentaires-filtres">
        <Button size="sm" variant={filtreActif === "tous" ? "default" : "outline"} aria-pressed={filtreActif === "tous"} data-testid="commentaires-filtre-tous" onClick={() => choisirFiltre("tous")}>Tous</Button>
        <Button size="sm" variant={filtreActif === "echanges" ? "default" : "outline"} aria-pressed={filtreActif === "echanges"} data-testid="commentaires-filtre-echanges" onClick={() => choisirFiltre("echanges")}>Échanges mail</Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 text-[var(--oh-text-muted)] animate-spin" /></div>
      ) : visibles.length === 0 ? (
        <div className="text-center py-8" data-testid="commentaires-vide">
          <MessageSquare className="w-12 h-12 mx-auto text-[var(--oh-text-secondary)] mb-3" />
          <p className="text-[var(--oh-text-muted)]">{filtreActif === "echanges" ? "Aucun échange mail" : "Aucun commentaire"}</p>
          <p className="text-[var(--oh-text-secondary)] text-sm mt-1">{filtreActif === "echanges" ? "Les mails reçus et les réponses envoyées depuis le Bureau AMO apparaîtront ici" : "Ajoutez des notes sur ce dossier"}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibles.map((comment, idx) => (
            <div key={comment.id || idx} className="p-4 bg-[var(--oh-bg-elevated)] rounded-lg">
              <p className="text-[var(--oh-text)] whitespace-pre-wrap">{comment.contenu}</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-[var(--oh-text-muted)]">
                <span>{comment.auteur_nom || "Utilisateur"}</span>
                <span>•</span>
                <span>{fmtDate(comment.created_at)}</span>
              </div>
              {estEchange(comment) && (
                <div style={{ marginTop: 8 }}>
                  <Bouton type="secondaire" data-testid="commentaire-mail-origine" aria-label="Voir le mail d'origine" style={{ minHeight: 44 }}
                    onClick={() => setMailVu({ ref: refMailDuCommentaire(comment) })}>Voir le mail d'origine</Bouton>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <MailOriginePanneau ouvert={Boolean(mailVu)} refCarte={mailVu?.ref || ""} onFerme={() => setMailVu(null)} />
    </div>
  );
}
